import React, { forwardRef, useImperativeHandle, useContext, useRef, useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import AppBreadCrumb from './AppBreadCrumb';
import { LayoutContext } from './context/layoutcontext';
import { useMasking } from './context/maskingcontext';
import { StyleClass } from 'primereact/styleclass';
import { Ripple } from 'primereact/ripple';
import { Menu } from 'primereact/menu';
import { Avatar } from 'primereact/avatar';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Password } from 'primereact/password';
import { InputOtp } from 'primereact/inputotp';
import { ProgressBar } from 'primereact/progressbar';
import { Toast } from 'primereact/toast';
import { Tag } from 'primereact/tag';
import { Badge } from 'primereact/badge';
import { useProfileCompletion } from '../hooks/useProfileCompletion';
import { useMessaging } from '../contexts/MessagingContext';
import { useNotifications } from '../contexts/NotificationContext';
import ImpersonationBanner from '../components/ImpersonationBanner';
import CompanyImpersonationBanner from '../components/company/CompanyImpersonationBanner';
import ReferralLinkDialog from '../components/ReferralLinkDialog';
import { isLendingNetworkPath } from '../components/MobileBottomNav';
import Swal from 'sweetalert2';

const AppTopbar = forwardRef((props, ref) => {
    const navigate = useNavigate();
    const location = useLocation();
    const btnRef1 = useRef(null);
    const btnRef2 = useRef(null);
    const menubuttonRef = useRef(null);
    const profileRef = useRef(null);
    const profileNavRef = useRef(null); // dropdown for "Profile" nav item
    const toast = useRef(null);

    // Mirrors the "Me" popup's open state so the trigger can expose
    // aria-expanded — assistive tech has no other way to know it opened.
    const [profileNavOpen, setProfileNavOpen] = useState(false);

    const { onMenuToggle, toggleSearch, showRightSidebar, layoutConfig, layoutState } = useContext(LayoutContext);

    // Masking context hook
    const {
        isMasked,
        isVerifying,
        isSettingPin,
        isResettingPin,
        hasPin,
        showPasswordDialog: maskingPinDialog,
        showPinSetupDialog,
        showChangePinDialog,
        showForgotPinDialog,
        toggleMasking,
        verifyPin,
        setMaskingPin,
        hidePasswordDialog: hideMaskingDialog,
        hidePinSetupDialog,
        showChangePin,
        hideChangePinDialog,
        showForgotPin,
        hideForgotPinDialog,
        resetPinWithPassword
    } = useMasking();

    // State for masking PIN input
    const [maskingPinInput, setMaskingPinInput] = useState('');
    const [maskingError, setMaskingError] = useState('');

    // State for PIN setup
    const [newPinInput, setNewPinInput] = useState('');
    const [confirmPinInput, setConfirmPinInput] = useState('');
    const [pinSetupError, setPinSetupError] = useState('');

    // State for Change PIN
    const [currentPinInput, setCurrentPinInput] = useState('');
    const [changePinNew, setChangePinNew] = useState('');
    const [changePinConfirm, setChangePinConfirm] = useState('');
    const [changePinError, setChangePinError] = useState('');

    // State for Forgot PIN
    const [forgotPinPassword, setForgotPinPassword] = useState('');
    const [forgotPinNew, setForgotPinNew] = useState('');
    const [forgotPinConfirm, setForgotPinConfirm] = useState('');
    const [forgotPinError, setForgotPinError] = useState('');

    // Profile completion hook
    const { profileCompletion, loading: profileLoading } = useProfileCompletion();

    // Messaging unread count from context (polls every 10 seconds)
    const { unreadCount: unreadMsgCount } = useMessaging();

    // Connection and feed notification counts (polls every 30 seconds)
    const { pendingConnectionsCount, newFeedPostsCount, markConnectionsViewed, markFeedViewed } = useNotifications();

    // Lending Network page detection — single source of truth in
    // MobileBottomNav.jsx so the topbar strip and bottom bar stay paired.
    const isLendingNetworkPage = isLendingNetworkPath(location.pathname);

    // State variables
    const [changePasswordDialog, setChangePasswordDialog] = useState(false);
    const [referralLinkDialogVisible, setReferralLinkDialogVisible] = useState(false); // MLM Phase 2
    const [loading, setLoading] = useState(false);
    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [passwordStrength, setPasswordStrength] = useState(0);
    const [avatarError, setAvatarError] = useState(false);

    // Get user from localStorage or context
    const user = JSON.parse(localStorage.getItem('user') || '{}');

    // "Profile" nav button dropdown
    const profileNavItems = [
        {
            label: 'My Profile',
            icon: 'pi pi-user',
            command: () => navigate('/app/my-agent-profile'),
        },
    ];

    // Profile menu items
    const profileItems = [
        {
            label: 'Profile',
            icon: 'pi pi-user',
            command: () => {
                navigate('/app/profile');
            }
        },
        {
            label: 'Change Password',
            icon: 'pi pi-lock',
            command: () => {
                setChangePasswordDialog(true);
            }
        },
        {
            label: 'Change Security PIN',
            icon: 'pi pi-key',
            command: () => {
                if (hasPin) {
                    showChangePin();
                } else {
                    toast.current?.show({
                        severity: 'info',
                        summary: 'No PIN Set',
                        detail: 'You need to set a security PIN first. Click the eye icon to set one.',
                        life: 4000
                    });
                }
            }
        },
        {
            label: 'Billing History',
            icon: 'pi pi-file-pdf',
            command: () => {
                navigate('/app/billing-history');
            }
        },
        // MLM Phase 2: Get My Referral Link - Available to ALL users
        {
            label: 'Get My Referral Link',
            icon: 'pi pi-share-alt',
            command: () => {
                setReferralLinkDialogVisible(true);
            }
        },
        {
            separator: true
        },
        {
            label: 'Logout',
            icon: 'pi pi-sign-out',
            command: () => {
                localStorage.clear();
                window.location.href = '/app/login';
            }
        }
    ];
    
    // Password validation functions
    const handlePasswordChange = (value) => {
        setPasswordData({ ...passwordData, newPassword: value });
        // Calculate password strength
        let strength = 0;
        if (value.length >= 8) strength += 25;
        if (/[a-z]/.test(value)) strength += 25;
        if (/[A-Z]/.test(value)) strength += 25;
        if (/[0-9]/.test(value) || /[^a-zA-Z0-9]/.test(value)) strength += 25;
        setPasswordStrength(strength);
    };
    
    const getPasswordStrengthColor = () => {
        if (passwordStrength < 30) return 'danger';
        if (passwordStrength < 60) return 'warning';
        if (passwordStrength < 90) return 'info';
        return 'success';
    };
    
    const getPasswordStrengthLabel = () => {
        if (passwordStrength < 30) return 'Weak';
        if (passwordStrength < 60) return 'Fair';
        if (passwordStrength < 90) return 'Good';
        return 'Strong';
    };
    
    const resetPasswordDialog = () => {
        setChangePasswordDialog(false);
        setPasswordData({
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
        });
        setPasswordStrength(0);
    };
    
    const handleChangePassword = async () => {
        if (passwordData.newPassword !== passwordData.confirmPassword) {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Passwords do not match',
                life: 3000
            });
            return;
        }
        
        setLoading(true);
        try {
            // API call to change password
            const response = await fetch('/api/change-password', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                },
                body: JSON.stringify({
                    current_password: passwordData.currentPassword,
                    password: passwordData.newPassword,
                    password_confirmation: passwordData.confirmPassword
                })
            });
            
            if (response.ok) {
                // Show SweetAlert success message
                Swal.fire({
                    title: 'Password Changed Successfully!',
                    text: 'Your password has been updated successfully',
                    icon: 'success',
                    timer: 2000,
                    timerProgressBar: true,
                    showConfirmButton: false,
                    allowOutsideClick: false,
                    allowEscapeKey: false
                });
                
                // Close modal and SweetAlert after short delay
                setTimeout(() => {
                    Swal.close();
                    resetPasswordDialog();
                }, 1800);
            } else {
                throw new Error('Failed to change password');
            }
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to change password',
                life: 3000
            });
        } finally {
            setLoading(false);
        }
    };
    

    // Handle masking PIN verification
    const handleMaskingPinSubmit = async () => {
        if (!maskingPinInput || maskingPinInput.length !== 4) {
            setMaskingError('Please enter your 4-digit PIN');
            return;
        }

        setMaskingError('');
        const result = await verifyPin(maskingPinInput);

        if (result.success) {
            setMaskingPinInput('');
            toast.current?.show({
                severity: 'success',
                summary: 'Unlocked',
                detail: 'Data is now visible. Auto-locks after 10 minutes of inactivity.',
                life: 4000
            });
        } else if (result.pinNotSet) {
            // PIN not set - the context will show setup dialog
            setMaskingPinInput('');
        } else {
            setMaskingError(result.message || 'Invalid PIN');
        }
    };

    // Handle PIN setup
    const handlePinSetupSubmit = async () => {
        if (!newPinInput || newPinInput.length !== 4) {
            setPinSetupError('Please enter a 4-digit PIN');
            return;
        }

        if (newPinInput !== confirmPinInput) {
            setPinSetupError('PINs do not match');
            return;
        }

        setPinSetupError('');
        const result = await setMaskingPin(newPinInput, confirmPinInput);

        if (result.success) {
            setNewPinInput('');
            setConfirmPinInput('');
            toast.current?.show({
                severity: 'success',
                summary: 'PIN Set',
                detail: 'Your security PIN has been set successfully. You can now use it to unlock sensitive data.',
                life: 4000
            });
        } else {
            setPinSetupError(result.message || 'Failed to set PIN');
        }
    };

    // Reset masking dialog state
    const resetMaskingDialog = () => {
        setMaskingPinInput('');
        setMaskingError('');
        hideMaskingDialog();
    };

    // Reset PIN setup dialog state
    const resetPinSetupDialog = () => {
        setNewPinInput('');
        setConfirmPinInput('');
        setPinSetupError('');
        hidePinSetupDialog();
    };

    // Handle Change PIN submit
    const handleChangePinSubmit = async () => {
        if (!currentPinInput || currentPinInput.length !== 4) {
            setChangePinError('Please enter your current 4-digit PIN');
            return;
        }
        if (!changePinNew || changePinNew.length !== 4) {
            setChangePinError('Please enter a new 4-digit PIN');
            return;
        }
        if (changePinNew !== changePinConfirm) {
            setChangePinError('New PINs do not match');
            return;
        }

        setChangePinError('');
        const result = await setMaskingPin(changePinNew, changePinConfirm, currentPinInput);

        if (result.success) {
            resetChangePinDialog();
            toast.current?.show({
                severity: 'success',
                summary: 'PIN Changed',
                detail: 'Your security PIN has been updated successfully.',
                life: 4000
            });
        } else {
            setChangePinError(result.message || 'Failed to change PIN');
        }
    };

    // Reset Change PIN dialog state
    const resetChangePinDialog = () => {
        setCurrentPinInput('');
        setChangePinNew('');
        setChangePinConfirm('');
        setChangePinError('');
        hideChangePinDialog();
    };

    // Handle Forgot PIN submit
    const handleForgotPinSubmit = async () => {
        if (!forgotPinPassword) {
            setForgotPinError('Please enter your login password');
            return;
        }
        if (!forgotPinNew || forgotPinNew.length !== 4) {
            setForgotPinError('Please enter a new 4-digit PIN');
            return;
        }
        if (forgotPinNew !== forgotPinConfirm) {
            setForgotPinError('New PINs do not match');
            return;
        }

        setForgotPinError('');
        const result = await resetPinWithPassword(forgotPinPassword, forgotPinNew, forgotPinConfirm);

        if (result.success) {
            resetForgotPinDialog();
            toast.current?.show({
                severity: 'success',
                summary: 'PIN Reset',
                detail: 'Your security PIN has been reset successfully.',
                life: 4000
            });
        } else {
            if (result.oauthUser) {
                setForgotPinError('Cannot reset PIN: No password set for this account. Please contact support.');
            } else {
                setForgotPinError(result.message || 'Failed to reset PIN');
            }
        }
    };

    // Reset Forgot PIN dialog state
    const resetForgotPinDialog = () => {
        setForgotPinPassword('');
        setForgotPinNew('');
        setForgotPinConfirm('');
        setForgotPinError('');
        hideForgotPinDialog();
    };

    useImperativeHandle(ref, () => ({
        menubutton: menubuttonRef.current
    }));


    return (
        <>
            {/* Tenant Admin Impersonation Banner */}
            <ImpersonationBanner />

            {/* Company Admin Impersonation Banner */}
            <CompanyImpersonationBanner />

            <div className="layout-topbar">
            <div className="topbar-left">
                <button ref={menubuttonRef} type="button" className="menu-button p-link" onClick={onMenuToggle}>
                    <i className={layoutState.staticMenuDesktopInactive ? "pi pi-chevron-right" : "pi pi-chevron-left"}></i>
                </button>

                <Link to="/app" className="horizontal-logo">
                    <img id="logo-horizontal" src={`${process.env.PUBLIC_URL}/layout/images/interestng_logo_updated.jpeg`} alt="INTERESTNG" style={{ height: '32px', width: 'auto' }} />
                </Link>

                <span className="topbar-separator"></span>

                <AppBreadCrumb />
                <img id="logo-mobile" className="mobile-logo" src={`${process.env.PUBLIC_URL}/layout/images/interestng_logo_updated.jpeg`} alt="INTERESTNG" style={{ height: '32px', width: 'auto' }} />
            </div>
            {/* Note: a duplicate <AppSidebar /> used to live here as a Diamond-template
                horizontal-menu artifact. Removed — the canonical sidebar is mounted
                in DashboardLayout.jsx and CSS already handles desktop/mobile visibility.
                Two instances were each fetching the menu independently, causing the
                Borrower Reports categories to drift between viewports. */}
            <div className="layout-mask modal-in"></div>

            <div className="topbar-right">
                {/* Mobile-only breadcrumb — on mobile the topbar is two rows
                    (Diamond template): row 1 = menu + logo, row 2 = topbar-right.
                    This renders the page breadcrumb on that second row, on the
                    same line as the masking toggle + avatar. Hidden ≥992px via
                    CSS, where the breadcrumb already lives in topbar-left. */}
                <div className="topbar-mobile-breadcrumb">
                    <AppBreadCrumb />
                </div>

                {/* Lending Network nav icons - only visible on Lending Network pages */}
                {isLendingNetworkPage && (
                    <>
                        {/* Real <button>s, not clickable <li>s: a button is focusable
                            and fires onClick for both Enter and Space natively, so this
                            strip is reachable by keyboard without hand-rolled key handlers. */}
                        <nav className="topbar-nav-icons" aria-label="Lending Network">
                            <button
                                type="button"
                                className={`topbar-nav-item${location.pathname.startsWith('/app/business/pages') ? ' active' : ''}`}
                                onClick={() => navigate('/app/business/pages')}
                                title="For Business"
                                aria-current={location.pathname.startsWith('/app/business/pages') ? 'page' : undefined}
                                data-testid="my-company-link"
                            >
                                <i className="pi pi-building nav-icon"></i>
                                <span className="nav-label">For Business</span>
                            </button>
                            <button
                                type="button"
                                className={`topbar-nav-item${location.pathname.includes('/leadflow/feed') ? ' active' : ''}`}
                                onClick={() => {
                                    markFeedViewed();
                                    navigate('/app/leadflow/feed');
                                }}
                                title="Lending Feed"
                                aria-current={location.pathname.includes('/leadflow/feed') ? 'page' : undefined}
                            >
                                <span className="nav-icon" style={{ position: 'relative', display: 'inline-block' }}>
                                    <i className="pi pi-comments"></i>
                                    {newFeedPostsCount > 0 && (
                                        <Badge value={newFeedPostsCount > 99 ? '99+' : newFeedPostsCount} severity="danger" />
                                    )}
                                </span>
                                <span className="nav-label">Feed</span>
                            </button>
                            <button
                                type="button"
                                className={`topbar-nav-item${location.pathname === '/app/my-network' ? ' active' : ''}`}
                                onClick={() => {
                                    markConnectionsViewed();
                                    navigate('/app/my-network');
                                }}
                                title="Lending Connections"
                                aria-current={location.pathname === '/app/my-network' ? 'page' : undefined}
                            >
                                <span className="nav-icon" style={{ position: 'relative', display: 'inline-block' }}>
                                    <i className="pi pi-users"></i>
                                    {pendingConnectionsCount > 0 && (
                                        <Badge value={pendingConnectionsCount > 99 ? '99+' : pendingConnectionsCount} severity="danger" />
                                    )}
                                </span>
                                <span className="nav-label">Connections</span>
                            </button>
                            <button
                                type="button"
                                className={`topbar-nav-item${location.pathname === '/app/messaging' ? ' active' : ''}`}
                                onClick={() => navigate('/app/messaging')}
                                title="Messages"
                                aria-current={location.pathname === '/app/messaging' ? 'page' : undefined}
                            >
                                <span className="nav-icon" style={{ position: 'relative', display: 'inline-block' }}>
                                    <i className="pi pi-envelope"></i>
                                    {unreadMsgCount > 0 && (
                                        <Badge value={unreadMsgCount > 99 ? '99+' : unreadMsgCount} severity="danger" />
                                    )}
                                </span>
                                <span className="nav-label">Messages</span>
                            </button>
                            {/* Not a destination — opens the profile popup, so it is
                                announced as a menu trigger rather than a page link. */}
                            <button
                                type="button"
                                className={`topbar-nav-item${location.pathname === '/app/my-agent-profile' ? ' active' : ''}`}
                                onClick={(e) => profileNavRef.current?.toggle(e)}
                                title="Profile"
                                aria-haspopup="true"
                                aria-expanded={profileNavOpen}
                                style={{ position: 'relative' }}
                                data-testid="me-nav-dropdown"
                            >
                                <i className="pi pi-user nav-icon"></i>
                                <span className="nav-label">
                                    Me <i className="pi pi-angle-down" style={{ fontSize: '0.65rem', verticalAlign: 'middle' }} />
                                </span>
                            </button>
                        </nav>
                        {/* Profile nav dropdown */}
                        <Menu
                            ref={profileNavRef}
                            model={profileNavItems}
                            popup
                            className="w-12rem"
                            onShow={() => setProfileNavOpen(true)}
                            onHide={() => setProfileNavOpen(false)}
                        />
                        <span className="topbar-separator"></span>
                    </>
                )}

                <ul className="topbar-menu">
                    {/* Data Masking Toggle */}
                    <li className="masking-toggle-item mr-2">
                        <Button
                            className={`p-button-rounded p-button-text ${isMasked ? 'p-button-secondary' : 'p-button-success'}`}
                            onClick={toggleMasking}
                            tooltip={isMasked ? 'Click to unmask data (requires PIN)' : 'Click to mask sensitive data'}
                            tooltipOptions={{ position: 'bottom' }}
                            aria-label={isMasked ? 'Unmask Data' : 'Mask Data'}
                        >
                            <i className={`pi ${isMasked ? 'pi-eye-slash' : 'pi-eye'}`} style={{ fontSize: '1.2rem' }}></i>
                        </Button>
                    </li>

                    {/* Profile Completion Indicator
                        - Shows on ALL devices when profile not 100% complete
                        - Mobile: Compact percentage tag only
                        - Desktop: Progress bar + percentage
                        - 100% complete: Nothing shown
                    */}
                    {profileCompletion && !profileCompletion.isComplete && (
                        <li className="profile-completion-item mr-1 sm:mr-2 md:mr-3 flex align-items-center">
                            {/* Mobile/Tablet: Compact percentage tag only */}
                            <Tag
                                severity={profileCompletion.percentage < 50 ? 'danger' : profileCompletion.percentage < 75 ? 'warning' : 'info'}
                                value={`${profileCompletion.percentage}%`}
                                className="profile-percent-mobile md:hidden"
                                onClick={() => navigate('/app/profile')}
                                style={{ cursor: 'pointer' }}
                            />
                            {/* Desktop: Progress bar + percentage */}
                            <Button
                                className="p-button-text p-button-warning p-button-sm hidden md:flex"
                                onClick={() => navigate('/app/profile')}
                                aria-label="Complete Profile"
                            >
                                <div className="flex align-items-center gap-2">
                                    <ProgressBar
                                        value={profileCompletion.percentage}
                                        style={{ width: '80px', height: '8px' }}
                                        showValue={false}
                                        aria-label="Profile completion" />
                                    <Tag
                                        severity={profileCompletion.percentage < 50 ? 'danger' : profileCompletion.percentage < 75 ? 'warning' : 'info'}
                                        value={`${profileCompletion.percentage}%`}
                                        className="text-xs"
                                    />
                                </div>
                            </Button>
                        </li>
                    )}

                    <li className="profile-item">
                        <Button
                            className="p-button-text profile-button"
                            onClick={(e) => profileRef.current?.toggle(e)}
                            aria-label="User Profile"
                        >
                            {user?.avatar && !avatarError ? (
                                <img
                                    src={user.avatar}
                                    alt="profile"
                                    className="profile-image"
                                    onError={() => setAvatarError(true)}
                                />
                            ) : (
                                <Avatar
                                    label={user?.name ? user.name.charAt(0).toUpperCase() : 'U'}
                                    shape="circle"
                                    size="normal"
                                    style={{ backgroundColor: 'var(--primary-color)', color: '#ffffff' }}
                                    className="profile-image"
                                />
                            )}
                            <span className="profile-name d-none d-md-inline">{user?.name || 'User'}</span>
                        </Button>
                    </li>
                </ul>
            </div>

            {/* Profile Menu */}
            <Menu 
                ref={profileRef} 
                model={profileItems} 
                popup 
                className="w-15rem"
            />


            {/* Change Password Dialog */}
            <Dialog
                visible={changePasswordDialog}
                style={{ width: '450px' }}
                header="Change Password"
                modal
                className="p-fluid"
                footer={
                    <div>
                        <Button 
                            label="Cancel" 
                            icon="pi pi-times" 
                            className="p-button-text" 
                            onClick={resetPasswordDialog}
                            disabled={loading}
                        />
                        <Button 
                            label="Change Password" 
                            icon="pi pi-check" 
                            onClick={handleChangePassword}
                            loading={loading}
                        />
                    </div>
                }
                onHide={resetPasswordDialog}
            >
                <div className="field">
                    <label htmlFor="currentPassword">Current Password</label>
                    <Password
                        id="currentPassword"
                        value={passwordData.currentPassword}
                        onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                        toggleMask
                        feedback={false}
                        placeholder="Enter current password"
                    />
                </div>

                <div className="field">
                    <label htmlFor="newPassword">New Password</label>
                    <Password
                        id="newPassword"
                        value={passwordData.newPassword}
                        onChange={(e) => handlePasswordChange(e.target.value)}
                        toggleMask
                        feedback={false}
                        placeholder="Enter new password"
                    />
                    {passwordData.newPassword && (
                        <div className="mt-2">
                            <div className="flex justify-content-between align-items-center mb-1">
                                <small>Password Strength</small>
                                <small className={`text-${getPasswordStrengthColor()}`}>
                                    {getPasswordStrengthLabel()}
                                </small>
                            </div>
                            <ProgressBar
                                value={passwordStrength}
                                showValue={false}
                                style={{ height: '6px' }}
                                className={`bg-${getPasswordStrengthColor()}`}
                                aria-label="Password strength" />
                            <small className="block mt-2 text-600">
                                Use at least 8 characters with a mix of letters, numbers and symbols
                            </small>
                        </div>
                    )}
                </div>

                <div className="field">
                    <label htmlFor="confirmPassword">Confirm New Password</label>
                    <Password
                        id="confirmPassword"
                        value={passwordData.confirmPassword}
                        onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                        toggleMask
                        feedback={false}
                        placeholder="Confirm new password"
                    />
                    {passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword && (
                        <small className="p-error">Passwords do not match</small>
                    )}
                </div>
            </Dialog>

            {/* MLM Phase 2: Referral Link Dialog */}
            <ReferralLinkDialog
                visible={referralLinkDialogVisible}
                onHide={() => setReferralLinkDialogVisible(false)}
            />

            {/* Data Masking PIN Verification Dialog */}
            <Dialog
                visible={maskingPinDialog}
                style={{ width: '400px' }}
                header={
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-lock text-primary" style={{ fontSize: '1.5rem' }}></i>
                        <span>Unlock Sensitive Data</span>
                    </div>
                }
                modal
                className="p-fluid"
                onShow={() => {
                    // Clear input when dialog opens to prevent browser autofill
                    setMaskingPinInput('');
                    setMaskingError('');
                }}
                footer={
                    <div className="flex justify-content-between align-items-center">
                        <Button
                            label="Forgot PIN?"
                            className="p-button-text p-button-secondary"
                            onClick={showForgotPin}
                            disabled={isVerifying}
                        />
                        <div className="flex gap-2">
                            <Button
                                label="Cancel"
                                icon="pi pi-times"
                                className="p-button-text"
                                onClick={resetMaskingDialog}
                                disabled={isVerifying}
                            />
                            <Button
                                label="Unlock"
                                icon="pi pi-unlock"
                                onClick={handleMaskingPinSubmit}
                                loading={isVerifying}
                                disabled={!maskingPinInput || maskingPinInput.length !== 4}
                            />
                        </div>
                    </div>
                }
                onHide={resetMaskingDialog}
            >
                <div className="mb-3">
                    <p className="text-600 m-0">
                        Enter your 4-digit security PIN to view unmasked data.
                        Data will auto-lock after 10 minutes of inactivity.
                    </p>
                </div>

                <div className="field">
                    <label htmlFor="maskingPin" className="font-medium mb-2 block">Security PIN</label>
                    <div className="flex justify-content-center">
                        <InputOtp
                            id="maskingPin"
                            name="security-pin-verify"
                            value={maskingPinInput}
                            onChange={(e) => {
                                setMaskingPinInput(e.value);
                                setMaskingError('');
                            }}
                            length={4}
                            integerOnly
                            mask
                            autoFocus
                            autoComplete="new-password"
                            className={maskingError ? 'p-invalid' : ''}
                        />
                    </div>
                    {maskingError && (
                        <small className="p-error block mt-2 text-center">{maskingError}</small>
                    )}
                </div>

                <div className="flex align-items-center gap-2 mt-3 p-2 surface-100 border-round">
                    <i className="pi pi-info-circle text-primary"></i>
                    <small className="text-600">
                        This protects sensitive information like PAN, Aadhaar, phone numbers, and email addresses.
                    </small>
                </div>
            </Dialog>

            {/* PIN Setup Dialog */}
            <Dialog
                visible={showPinSetupDialog}
                style={{ width: '450px' }}
                header={
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-shield text-primary" style={{ fontSize: '1.5rem' }}></i>
                        <span>Set Up Security PIN</span>
                    </div>
                }
                modal
                className="p-fluid"
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            className="p-button-text"
                            onClick={resetPinSetupDialog}
                            disabled={isSettingPin}
                        />
                        <Button
                            label="Set PIN"
                            icon="pi pi-check"
                            onClick={handlePinSetupSubmit}
                            loading={isSettingPin}
                            disabled={!newPinInput || newPinInput.length !== 4 || !confirmPinInput || confirmPinInput.length !== 4}
                        />
                    </div>
                }
                onHide={resetPinSetupDialog}
            >
                <div className="mb-4">
                    <p className="text-600 m-0">
                        Create a 4-digit security PIN to protect your sensitive data.
                        You'll use this PIN to unlock masked information.
                    </p>
                </div>

                <div className="field mb-4">
                    <label htmlFor="newPin" className="font-medium mb-2 block">Enter New PIN</label>
                    <div className="flex justify-content-center">
                        <InputOtp
                            id="newPin"
                            name="setup-new-pin"
                            value={newPinInput}
                            onChange={(e) => {
                                setNewPinInput(e.value);
                                setPinSetupError('');
                            }}
                            length={4}
                            integerOnly
                            mask
                            autoFocus
                            autoComplete="new-password"
                        />
                    </div>
                </div>

                <div className="field">
                    <label htmlFor="confirmPin" className="font-medium mb-2 block">Confirm PIN</label>
                    <div className="flex justify-content-center">
                        <InputOtp
                            id="confirmPin"
                            name="setup-confirm-pin"
                            value={confirmPinInput}
                            onChange={(e) => {
                                setConfirmPinInput(e.value);
                                setPinSetupError('');
                            }}
                            length={4}
                            integerOnly
                            mask
                            autoComplete="new-password"
                        />
                    </div>
                    {newPinInput && confirmPinInput && newPinInput !== confirmPinInput && (
                        <small className="p-error block mt-2 text-center">PINs do not match</small>
                    )}
                    {pinSetupError && (
                        <small className="p-error block mt-2 text-center">{pinSetupError}</small>
                    )}
                </div>

                <div className="flex align-items-center gap-2 mt-4 p-2 surface-100 border-round">
                    <i className="pi pi-info-circle text-primary"></i>
                    <small className="text-600">
                        Remember this PIN! You'll need it to view sensitive data like PAN, phone numbers, and email addresses.
                    </small>
                </div>
            </Dialog>

            {/* Change PIN Dialog */}
            <Dialog
                visible={showChangePinDialog}
                style={{ width: '450px' }}
                header={
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-key text-primary" style={{ fontSize: '1.5rem' }}></i>
                        <span>Change Security PIN</span>
                    </div>
                }
                modal
                className="p-fluid"
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            className="p-button-text"
                            onClick={resetChangePinDialog}
                            disabled={isSettingPin}
                        />
                        <Button
                            label="Change PIN"
                            icon="pi pi-check"
                            onClick={handleChangePinSubmit}
                            loading={isSettingPin}
                            disabled={!currentPinInput || currentPinInput.length !== 4 || !changePinNew || changePinNew.length !== 4 || !changePinConfirm || changePinConfirm.length !== 4}
                        />
                    </div>
                }
                onHide={resetChangePinDialog}
            >
                <div className="mb-4">
                    <p className="text-600 m-0">
                        Enter your current PIN and choose a new 4-digit security PIN.
                    </p>
                </div>

                <div className="field mb-4">
                    <label htmlFor="currentPin" className="font-medium mb-2 block">Current PIN</label>
                    <div className="flex justify-content-center">
                        <InputOtp
                            id="currentPin"
                            name="change-current-pin"
                            value={currentPinInput}
                            onChange={(e) => {
                                setCurrentPinInput(e.value);
                                setChangePinError('');
                            }}
                            length={4}
                            integerOnly
                            mask
                            autoFocus
                            autoComplete="new-password"
                        />
                    </div>
                </div>

                <div className="field mb-4">
                    <label htmlFor="changeNewPin" className="font-medium mb-2 block">New PIN</label>
                    <div className="flex justify-content-center">
                        <InputOtp
                            id="changeNewPin"
                            name="change-new-pin"
                            value={changePinNew}
                            onChange={(e) => {
                                setChangePinNew(e.value);
                                setChangePinError('');
                            }}
                            length={4}
                            integerOnly
                            mask
                            autoComplete="new-password"
                        />
                    </div>
                </div>

                <div className="field">
                    <label htmlFor="changeConfirmPin" className="font-medium mb-2 block">Confirm New PIN</label>
                    <div className="flex justify-content-center">
                        <InputOtp
                            id="changeConfirmPin"
                            name="change-confirm-pin"
                            value={changePinConfirm}
                            onChange={(e) => {
                                setChangePinConfirm(e.value);
                                setChangePinError('');
                            }}
                            length={4}
                            integerOnly
                            mask
                            autoComplete="new-password"
                        />
                    </div>
                    {changePinNew && changePinConfirm && changePinNew !== changePinConfirm && (
                        <small className="p-error block mt-2 text-center">PINs do not match</small>
                    )}
                    {changePinError && (
                        <small className="p-error block mt-2 text-center">{changePinError}</small>
                    )}
                </div>

                <div className="flex align-items-center gap-2 mt-4 p-2 surface-100 border-round">
                    <i className="pi pi-info-circle text-primary"></i>
                    <small className="text-600">
                        If you forgot your current PIN, close this dialog and use "Forgot PIN?" option.
                    </small>
                </div>
            </Dialog>

            {/* Forgot PIN Dialog */}
            <Dialog
                visible={showForgotPinDialog}
                style={{ width: '450px' }}
                header={
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-refresh text-primary" style={{ fontSize: '1.5rem' }}></i>
                        <span>Reset Security PIN</span>
                    </div>
                }
                modal
                className="p-fluid"
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            className="p-button-text"
                            onClick={resetForgotPinDialog}
                            disabled={isResettingPin}
                        />
                        <Button
                            label="Reset PIN"
                            icon="pi pi-check"
                            onClick={handleForgotPinSubmit}
                            loading={isResettingPin}
                            disabled={!forgotPinPassword || !forgotPinNew || forgotPinNew.length !== 4 || !forgotPinConfirm || forgotPinConfirm.length !== 4}
                        />
                    </div>
                }
                onHide={resetForgotPinDialog}
            >
                <div className="mb-4">
                    <p className="text-600 m-0">
                        Forgot your PIN? Enter your login password to verify your identity and set a new PIN.
                    </p>
                </div>

                <div className="field mb-4">
                    <label htmlFor="forgotPinPassword" className="font-medium mb-2 block">Login Password</label>
                    <Password
                        id="forgotPinPassword"
                        value={forgotPinPassword}
                        onChange={(e) => {
                            setForgotPinPassword(e.target.value);
                            setForgotPinError('');
                        }}
                        toggleMask
                        feedback={false}
                        placeholder="Enter your login password"
                        className="w-full"
                    />
                </div>

                <div className="field mb-4">
                    <label htmlFor="forgotNewPin" className="font-medium mb-2 block">New PIN</label>
                    <div className="flex justify-content-center">
                        <InputOtp
                            id="forgotNewPin"
                            name="forgot-new-pin"
                            value={forgotPinNew}
                            onChange={(e) => {
                                setForgotPinNew(e.value);
                                setForgotPinError('');
                            }}
                            length={4}
                            integerOnly
                            mask
                            autoComplete="new-password"
                        />
                    </div>
                </div>

                <div className="field">
                    <label htmlFor="forgotConfirmPin" className="font-medium mb-2 block">Confirm New PIN</label>
                    <div className="flex justify-content-center">
                        <InputOtp
                            id="forgotConfirmPin"
                            name="forgot-confirm-pin"
                            value={forgotPinConfirm}
                            onChange={(e) => {
                                setForgotPinConfirm(e.value);
                                setForgotPinError('');
                            }}
                            length={4}
                            integerOnly
                            mask
                            autoComplete="new-password"
                        />
                    </div>
                    {forgotPinNew && forgotPinConfirm && forgotPinNew !== forgotPinConfirm && (
                        <small className="p-error block mt-2 text-center">PINs do not match</small>
                    )}
                    {forgotPinError && (
                        <small className="p-error block mt-2 text-center">{forgotPinError}</small>
                    )}
                </div>

                <div className="flex align-items-center gap-2 mt-4 p-2 surface-100 border-round">
                    <i className="pi pi-info-circle text-warning"></i>
                    <small className="text-600">
                        If you signed up with Google and don't have a password, please contact support.
                    </small>
                </div>
            </Dialog>

            {/* Toast for notifications */}
            <Toast ref={toast} />
        </div>
        </>
    );
});

AppTopbar.displayName = 'AppTopbar';

export default AppTopbar;